import { CheckOutlined, EditOutlined, ReloadOutlined } from '@ant-design/icons'
import { Stack } from '@sureco/design-system'
import { Button, Table, Tooltip } from '@surecompanies/core_components'
import { Alert } from 'antd'
import styled from 'styled-components'
import type { ColumnsType } from 'antd/es/table'
import { useRouterContext } from 'components/RouterContext'
import { CopyableValue } from 'components/CopyableValue'
import BreadcrumbNav from 'enrollment-platform/components/Breadcrumbs'
import {
  BENEFIT_INTEGRATIONS,
  ideonAgentDetailPath,
  ideonConfigPath,
} from 'enrollment-platform/pages/BenefitIntegrations/paths'
import Page from 'layouts/Page'
import React, { useMemo } from 'react'
import type { AgentIndex } from 'enrollment-platform/api/partner-benefit-graph/ideon-agents.types'
import { formatIdeonAgentName } from './formatIdeonAgentName'
import { useIdeonAgents } from './hooks/useIdeonAgents'

const benefitIntegrationsListPath = () => `/${BENEFIT_INTEGRATIONS}`

// AntD's Alert with a description aligns all children to flex-start, which
// leaves the Retry action at the top. Center only the action so the icon and
// title/description keep their natural top alignment.
const CenteredActionAlert = styled.div`
  .ant-alert-action {
    align-self: center;
  }
`

export default function IdeonConfig() {
  const { router } = useRouterContext()
  const {
    agents,
    meta,
    page,
    perPage,
    loading: agentsLoading,
    loadError,
    missingManagedAgentEnvVars,
    setPage,
    setPerPage,
    refresh,
  } = useIdeonAgents()

  const breadcrumbItems = useMemo(
    () => [
      { title: 'Home', path: '/' },
      {
        title: 'Benefit Integrations',
        path: benefitIntegrationsListPath(),
      },
      // "Ideon" and "Configs" have no dedicated landing page yet — link them
      // to the Agents page so the breadcrumb chain stays traversable.
      { title: 'Ideon', path: ideonConfigPath() },
      { title: 'Configs', path: ideonConfigPath() },
      { title: 'Agents' },
    ],
    []
  )

  const agentsColumns = useMemo<ColumnsType<AgentIndex>>(
    () => [
      {
        title: 'Name',
        key: 'name',
        render: (_: unknown, row: AgentIndex) => (
          <CopyableValue value={formatIdeonAgentName(row)} />
        ),
      },
      {
        title: 'NPN',
        dataIndex: 'nationalProducerNumber',
        key: 'nationalProducerNumber',
        render: (npn?: string) => <CopyableValue value={npn} />,
      },
      {
        title: 'Ideon Agent ID',
        dataIndex: 'id',
        key: 'id',
        render: (id?: string) => <CopyableValue value={id} />,
      },
      {
        title: 'Primary',
        dataIndex: 'accountPrimary',
        key: 'accountPrimary',
        render: (v?: boolean) =>
          v ? (
            <CheckOutlined
              role="img"
              aria-label="Yes"
              style={{ color: '#52c41a' }}
            />
          ) : null,
      },
      {
        title: 'Managed',
        dataIndex: 'managed',
        key: 'managed',
        render: (v?: boolean) =>
          v ? (
            <CheckOutlined
              role="img"
              aria-label="Yes"
              style={{ color: '#52c41a' }}
            />
          ) : null,
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 100,
        render: (_: unknown, row: AgentIndex) => {
          const name = formatIdeonAgentName(row) || 'agent'
          return (
            <Tooltip title="Manage">
              <Button
                type="default"
                icon={<EditOutlined aria-hidden="true" />}
                onClick={() => router.push(ideonAgentDetailPath(row.id))}
                aria-label={`Manage ${name}`}
              />
            </Tooltip>
          )
        },
      },
    ],
    [router]
  )

  return (
    <Page
      title="Agents"
      maxWidth="100%"
      breadcrumb={<BreadcrumbNav items={breadcrumbItems} />}
    >
      <Stack direction="col" space={16}>
        {missingManagedAgentEnvVars.length > 0 ? (
          <Alert
            type="warning"
            showIcon
            message="Ideon managed agent env vars are not configured"
            description={
              <>
                The following env var
                {missingManagedAgentEnvVars.length > 1 ? 's are' : ' is'} not
                set: {missingManagedAgentEnvVars.join(', ')}. Edits to agents
                will fail until they are configured.
              </>
            }
          />
        ) : null}

        {!agentsLoading && loadError ? (
          <CenteredActionAlert>
            <Alert
              type="error"
              showIcon
              // role="alert" so screen readers announce the failure when the
              // alert appears — AntD's Alert doesn't add it by default.
              role="alert"
              message="Failed to load agents"
              description={loadError}
              action={
                <Button
                  icon={<ReloadOutlined aria-hidden="true" />}
                  onClick={() => void refresh()}
                >
                  Retry
                </Button>
              }
            />
          </CenteredActionAlert>
        ) : null}

        <Table
          rowKey={(r: AgentIndex) => r.id}
          loading={agentsLoading}
          dataSource={agents}
          columns={agentsColumns}
          locale={{ emptyText: 'No agents found' }}
          pagination={{
            current: page,
            pageSize: perPage,
            total: meta.total ?? 0,
            showSizeChanger: true,
            onChange: (nextPage: number, nextPageSize: number) => {
              if (nextPageSize !== perPage) {
                // Reset to page 1 so the user doesn't land on a now-empty page
                // when increasing the page size.
                setPerPage(nextPageSize)
                setPage(1)
              } else {
                setPage(nextPage)
              }
            },
          }}
        />
      </Stack>
    </Page>
  )
}
